import { StyledCard } from '@common/Configuration/FormValueConfiguration';
import { Entity } from '@common/types/Entity';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Button, Checkbox, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { clone } from '@prophecy/utils/data';
import React, { useEffect, useRef } from 'react';

import { useGlobalVariables } from '../../../../../context/globalConfig/securedConfigHook';
import { FileFormats, PipelineEntityType, TranspilerOptionsTypeWithConfig } from '../../../config';
import { useImportState } from '../../useImportState';
import { isInformatica as _isInformatica } from '../../utils';
import { getJobFolderType } from '../getJobFolderType';
import { StyledInput, StyledStackContainer } from '../styled';
import { ActionTypes } from '../types';
import { DeleteEntityDropdown, EntityRootHeading, getEntityErrorTitle, getValidateIcon, useToggleMap } from '../utils';
import { AssociatedEntity } from './AssociatedEntity';
import { EntityFileAndName } from './EntityFileAndName';
import { EntityHeading } from './EntityHeading';
import { PipelineConfiguration } from './PipelineConfiguration';
import { TranspilerOptions } from './TranspilerOptions';

export const EntityPipelines = ({
  pipelines,
  hasJobs: _hasJobs
}: {
  pipelines: PipelineEntityType[];
  hasJobs: boolean;
}) => {
  const {
    transpilerState: { pipelineWithErrors },
    transpileGraphState: {
      clientState: {
        validate: { validatedData }
      }
    },
    formDataWithActions: {
      formContext: { getValues }
    },
    actions: { updateTranspiledPipeline }
  } = useImportState();

  const source = getValues('source');
  const isSpark = [BackEndCodeLanguage.python, BackEndCodeLanguage.scala].includes(
    getValues('language') as BackEndCodeLanguage
  );
  const isSql = getValues('language') === BackEndCodeLanguage.sql;
  const isAbinitio = source === FileFormats.abinitio;
  const isJobEnabeld = useGlobalVariables().isTranspilerJobEnabled;
  const isInformatica = _isInformatica(source as FileFormats);
  const isInformaticaCloud = source === FileFormats.informaticaCloud;
  const fileType = getJobFolderType(source, !!isJobEnabeld)[0];
  const isAlteryx = source === FileFormats.alteryx;
  const isDatastage = source === FileFormats.datastage;
  const { toggleMap, handleToggleEntityState } = useToggleMap();
  const pipelineCount = pipelines.length;
  const hasDatasets = useGlobalVariables().isAlteryxDatasetEnabled;

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (pipelineCount > 0 && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [containerRef, pipelineCount]);

  if (pipelineCount < 1) {
    return null;
  }

  const handlePipelineChange = (value: string | boolean, key: string, index: number) => {
    const currentPipeline = pipelines[index];
    updateTranspiledPipeline(currentPipeline.__pathIndex as number, { ...pipelines[index], [key]: value });
  };

  const handleOptionChange = (
    value: string,
    index: number,
    pipelineIndex: number,
    key: keyof TranspilerOptionsTypeWithConfig,
    action?: ActionTypes
  ) => {
    const currentPipeline = pipelines[pipelineIndex];
    const currentPipelineOptions = pipelines[pipelineIndex]?.transpilerOptions || { pathsToBeReplaced: [] };
    const currentValues = clone(currentPipelineOptions?.[key] || []);
    if (action === ActionTypes.delete) {
      currentValues.splice(index, 1);
    } else if (action === ActionTypes.add) {
      currentValues.push(value);
    } else {
      currentValues[index] = value;
    }
    updateTranspiledPipeline(currentPipeline.__pathIndex as number, {
      ...pipelines[pipelineIndex],
      transpilerOptions: { ...currentPipelineOptions, [key]: currentValues }
    });
  };

  const currentEntity = Entity.Pipeline;

  const isDisabled = pipelines.some((pipeline) => !pipeline.enabled);
  return (
    <Stack width='100%' grow='1' gap={theme.spaces.x16}>
      <EntityRootHeading
        disabled={isDisabled}
        onDelete={() => updateTranspiledPipeline(0, pipelines[0], ActionTypes.deleteAll)}
        count={pipelines.length}
        entity={currentEntity}
        onChange={(checked) => updateTranspiledPipeline(0, pipelines[0], ActionTypes.all, checked)}
      />
      <StyledStackContainer ref={containerRef} gap={theme.spaces.x16} grow='1'>
        {pipelines.map((pipeline, index) => {
          const toggleKey = 'pipeline' + index;
          const error = pipelineWithErrors.find((p) => p.name === pipeline.name);
          const options = pipeline.transpilerOptions;
          const validatedPipeline = validatedData?.pipelines.find((pipeline1) => pipeline1.name === pipeline.name);
          const validateStatus = validatedPipeline?.validationStatus;
          const message = validatedPipeline?.errorMessage;
          const hasError = !!error;
          return (
            <Tooltip key={index + 'pipeline'} title={getEntityErrorTitle(error)}>
              <Stack gap={theme.spaces.x16}>
                <StyledCard hasError={hasError}>
                  <Stack gap={theme.spaces.x16} data-entityname={`${Entity.Pipeline}_${pipeline.name}`}>
                    <EntityHeading
                      entity={currentEntity}
                      transpiled={pipeline.transpiled}
                      enabled={pipeline.enabled}
                      onChange={(value, key) => handlePipelineChange(value, key, index)}
                      title={pipeline.name}
                      actions={
                        <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
                          {validateStatus !== undefined && getValidateIcon(validateStatus, message)}
                          <DeleteEntityDropdown
                            title={`Delete ${currentEntity}`}
                            name={pipeline.name}
                            onClick={() =>
                              updateTranspiledPipeline(pipeline.__pathIndex as number, undefined, ActionTypes.delete)
                            }
                          />
                        </Stack>
                      }
                      hasToggle={!toggleMap[toggleKey]}
                      toggle={() => handleToggleEntityState(toggleKey)}
                    />
                    {toggleMap[toggleKey] ? null : (
                      <>
                        <EntityFileAndName
                          error={error}
                          filePathValue={pipeline.filePath}
                          targetValue={pipeline.name}
                          type={fileType}
                          onChange={(value, key) => handlePipelineChange(value, key, index)}
                          targetName={`Target ${currentEntity} Name`}
                        />
                        {isDatastage && (
                          <StyledInput
                            label={`Xml ${currentEntity} Identifier`}
                            disabled={true}
                            value={pipeline.xmlPipelineIdentifier}
                            data-test-id='transpiler-entity-pipelines-xml-identifier-input'
                          />
                        )}
                        {isInformatica && (
                          <EntityFileAndName
                            filePathValue={pipeline.folderName}
                            targetValue={pipeline.xmlPipelineIdentifier}
                            type={fileType}
                            disabled={true}
                            sourceLabel='Folder Name'
                            onChange={() => null}
                            targetName={`${isInformaticaCloud ? '' : 'Xml'} ${currentEntity} Identifier`}
                          />
                        )}

                        <Stack gap={theme.spaces.x10}>
                          <Text level='sm13'>Entities associated to this {currentEntity.toLowerCase()}</Text>
                          <Stack direction='horizontal' gap={theme.spaces.x12}>
                            <AssociatedEntity entity={Entity.Job} path={pipeline.filePath} />
                            {hasDatasets && <AssociatedEntity entity={Entity.Dataset} path={pipeline.filePath} />}
                          </Stack>
                        </Stack>
                        {isAlteryx && (
                          <Stack gap={theme.spaces.x10} padding={`0 0 ${theme.spaces.x16}`}>
                            <Text level='sm'>Advanced options</Text>
                            <Stack direction='horizontal' width='100%' alignY='end' align='space-between'>
                              <Stack direction='vertical' gap={theme.spaces.x12}>
                                <Checkbox
                                  checked={Boolean(pipeline.transpileInterfaceTools)}
                                  onChange={(checked) =>
                                    handlePipelineChange(checked, 'transpileInterfaceTools', index)
                                  }>
                                  Transpile interface tools into configurations
                                </Checkbox>
                                {isAlteryx && (
                                  <>
                                    {isSql && (
                                      <>
                                        <Checkbox
                                          checked={Boolean(pipeline.transpileSourceTargetAsTable)}
                                          onChange={(checked) =>
                                            handlePipelineChange(checked, 'transpileSourceTargetAsTable', index)
                                          }>
                                          Transpile all sources to table gem
                                        </Checkbox>
                                        <Checkbox
                                          checked={Boolean(pipeline.breakPipelinesToSmallerPipelines)}
                                          onChange={(checked) => {
                                            handlePipelineChange(checked, 'breakPipelinesToSmallerPipelines', index);
                                          }}>
                                          Break into multiple workflows
                                        </Checkbox>
                                        {pipeline.breakPipelinesToSmallerPipelines ? (
                                          <Checkbox
                                            checked={Boolean(pipeline.preserveVisualContainers)}
                                            onChange={(checked) => {
                                              handlePipelineChange(checked, 'preserveVisualContainers', index);
                                            }}>
                                            Preserve Visual Containers
                                          </Checkbox>
                                        ) : null}
                                      </>
                                    )}
                                    {isSpark && (
                                      <Checkbox
                                        checked={Boolean(pipeline.convertVisualContainersToSubgraphs)}
                                        onChange={(checked) =>
                                          handlePipelineChange(checked, 'convertVisualContainersToSubgraphs', index)
                                        }>
                                        Convert visual containers to subgraphs
                                      </Checkbox>
                                    )}
                                  </>
                                )}
                              </Stack>
                              <Button
                                variant='secondaryGray'
                                size='s'
                                elementType='anchor'
                                href='https://transpiler.docs.prophecy.io/'
                                target='_blank'
                                data-test-id='entity-pipelines-learn-more-button'>
                                Learn More
                              </Button>
                            </Stack>
                          </Stack>
                        )}

                        {isAbinitio ? (
                          <>
                            <TranspilerOptions
                              parentPsetPath={pipeline.filePath}
                              heading={'Default Configuration Psets (*.psets file)'}
                              values={options?.targetPsets || []}
                              onChange={(value, valueIndex, action) =>
                                handleOptionChange(value, valueIndex, index, 'targetPsets', action)
                              }
                            />
                            <TranspilerOptions
                              heading={'Path to be replaced'}
                              parentPsetPath={pipeline.filePath}
                              btnText='Add Paths'
                              hasPath={true}
                              values={options?.pathsToBeReplaced || []}
                              onChange={(value, valueIndex, action) =>
                                handleOptionChange(value, valueIndex, index, 'pathsToBeReplaced', action)
                              }
                            />
                            <PipelineConfiguration pipeline={pipeline} />
                          </>
                        ) : null}
                      </>
                    )}
                  </Stack>
                </StyledCard>
              </Stack>
            </Tooltip>
          );
        })}
      </StyledStackContainer>
    </Stack>
  );
};
